/**
 * Applying one value to the same field on every line item.
 *
 * ATLAS requires packaging on EVERY position: type, count and shipping marks.
 * A commercial document states packaging once for the whole shipment ("Packed
 * in 1 carton", "Total Packages: 3 cartons") or not at all, so on a 27-item
 * proforma this is 81 values nobody will type by hand — and it is 81 of the 99
 * remaining validation errors.
 *
 * MEASURED against the validator on the 228407 proforma:
 *   as extracted ................................ 99 errors, 81 of them packaging
 *   same three values on all 27 positions ....... 18 errors, 0 packaging
 *   total on position 1 + packstuckverweis ...... 96 errors, 78 packaging
 * The third is why this exists: ATLAS's own shared-packaging reference does NOT
 * satisfy the validator, so the values genuinely have to be repeated.
 */

/** Fields that describe the shipment's packaging rather than the individual good. */
const BULK_FILLABLE_SUFFIXES = [
  'verpackung[0].artderVerpackung',
  'verpackung[0].anzahlderPackstucke',
  'verpackung[0].versandzeichen',
  // Quantity in the tariff's supplementary unit. ATLAS demands it on EVERY
  // position whenever the EZT prescribes a unit for the commodity code, and no
  // commercial document prints it — on the 27-item proforma that is 27 values
  // typed by hand, all of them "1" because each line is a single piece.
  //
  // MEASURED: "1" clears the error, "1,0" and "1.000" do not ("es sind nur
  // ganzzahlige Werte erlaubt"), and an empty field reports the requirement.
  //
  // Offered, never applied automatically: unlike the package TYPE, this is a
  // QUANTITY and lines can legitimately differ — spreading one silently would
  // repeat the mistake the package COUNT made. The button states how many
  // positions it will write, and the user decides.
  'mengeinbesondererMabeinheit',
];

const POSITION_PREFIX = /^position\[(\d+)\]\./;

/** True when this field may sensibly carry the same value on every line item. */
export function isBulkFillable(fieldPath: string): boolean {
  if (!POSITION_PREFIX.test(fieldPath)) return false;
  const suffix = fieldPath.replace(POSITION_PREFIX, '');
  return BULK_FILLABLE_SUFFIXES.includes(suffix);
}

/**
 * Every position's path for the same field, given how many positions exist.
 * Returns [] when the field is not position-scoped or the count is not sane,
 * so a caller can render the affordance only when it would do something.
 */
export function buildPositionPaths(fieldPath: string, positionCount: number): string[] {
  if (!POSITION_PREFIX.test(fieldPath)) return [];
  if (!Number.isInteger(positionCount) || positionCount <= 1) return [];
  const suffix = fieldPath.replace(POSITION_PREFIX, '');
  const paths: string[] = [];
  for (let i = 0; i < positionCount; i++) paths.push(`position[${i}].${suffix}`);
  return paths;
}

/**
 * The paths a bulk fill would actually change: every position except the ones
 * already holding this value. Filling is an explicit user action, so a value
 * already present elsewhere IS overwritten — only exact matches are skipped, to
 * keep the "changed N items" count honest.
 */
export function pathsNeedingFill(
  fieldPath: string,
  value: string,
  positionCount: number,
  currentValueAt: (path: string) => unknown,
): string[] {
  return buildPositionPaths(fieldPath, positionCount).filter((p) => {
    const existing = currentValueAt(p);
    return String(existing ?? '') !== String(value ?? '');
  });
}
